import * as React from 'react';
import { ChangeEvent, MouseEvent } from 'react';
import { Button, TextField } from '@origin-digital/style-guide';
import { AuthCard } from '../../../../components/Card';
import { Heading } from '../../../../components/Heading';
import { InfoText } from '../../../../components/InfoText';
import styled from 'styled-components';
import { LG } from '@origin-digital/helper';
import { onlyNumeric } from '@origin-digital/normalize';
import OTCPhone from 'images/otc_phone.svg';
import { Page } from '../../../../components/Page';
import { OneTimeCodeFooterView } from './OneTimeCodeFooterView';
import { OneTimeCodeTimer } from './OneTimeCodeTimer';
import { Box } from '@origin-digital/ods-core';
import { AlertContainer } from './AlertContainer';
import { BigPondAlert } from './BigPondAlert';
import { ResendCodeView } from './ResendCodeView';

interface OneTimeCodeViewProps {
  emailOrPhone: string;
  onResendClicked: (event: MouseEvent<HTMLAnchorElement>) => void;
  onTryAnotherClicked: (event: MouseEvent<HTMLAnchorElement>) => void;
  onFormSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onOneTimeCodeChanged?: (event: ChangeEvent<HTMLInputElement>) => void;
  onTimerComplete: () => void;
  hideResendOtcSuccessMessage: () => void;
  hideBigPondAlert: () => void;
  wrongCodeSubmitted: boolean;
  resendSpinner: boolean;
  submitSpinner: boolean;
  showTimer: boolean;
  countdown: number;
  analyticsTaskName: string;
  showResendOtcAlert: boolean;
  showResendOtcSuccess: boolean;
  showBigPondAlert: boolean;
  showResendFailedMessage: boolean;
  hideTryAgainLink?: boolean;
}

const OneTimeCodeView: React.FC<OneTimeCodeViewProps> = ({
  emailOrPhone,
  onResendClicked,
  onFormSubmit,
  onOneTimeCodeChanged,
  onTimerComplete,
  hideResendOtcSuccessMessage,
  hideBigPondAlert,
  wrongCodeSubmitted,
  resendSpinner,
  submitSpinner,
  showTimer,
  countdown,
  analyticsTaskName,
  onTryAnotherClicked,
  showResendOtcAlert,
  showResendOtcSuccess,
  showBigPondAlert,
  showResendFailedMessage,
  hideTryAgainLink = false,
}) => {
  return (
    <Page>
      <AuthCard image={OTCPhone}>
        <form data-id="onetimecode-form" onSubmit={onFormSubmit}>
          {showBigPondAlert && (
            <BigPondAlert analyticsTaskName={analyticsTaskName} hideAlert={hideBigPondAlert} />
          )}
          {showResendFailedMessage && (
            <AlertContainer data-id="alert-resend-failed" tone="critical">
              Sorry, something went wrong when we tried resending your one-time code. Please try
              again later.
            </AlertContainer>
          )}
          {showResendOtcAlert && (
            <AlertContainer data-id="alert-resend-blocked" tone="critical">
              Hold tight! Codes can take a while. Please wait for the countdown to finish before
              requesting another code.
            </AlertContainer>
          )}
          {showResendOtcSuccess && (
            <AlertContainer
              data-id="alert-resend-success"
              tone="positive"
              onCloseClick={hideResendOtcSuccessMessage}
            >
              Great! We’ve just sent a new code to {emailOrPhone}.
            </AlertContainer>
          )}
          <Heading text="Step 2: Add your code" />
          <InfoText
            text={`Your code's been sent to __${emailOrPhone}__, it can take a few minutes.`}
          />
          <TextField
            data-id="onetimecode-input"
            onChange={onOneTimeCodeChanged}
            label={'Enter your one-time code'}
            name={'onetimepasscode'}
            transform={onlyNumeric}
            disableIcon={true}
            type={'tel'}
            pattern={'[0-9]*'}
            maxLength={6}
            error={
              wrongCodeSubmitted
                ? "Hmm, it looks like that code isn't right or has expired. Please try again."
                : ''
            }
          />
          <Box display="flex" justifyContent="center" marginBottom="large">
            <OneTimeCodeTimer
              onTimerComplete={onTimerComplete}
              showTimer={showTimer}
              countdown={countdown}
            />
          </Box>
          <ButtonContainer>
            <Button
              data-id="onetimecode-submit"
              type="submit"
              text={'Verify account'}
              block={true}
              spinner={submitSpinner}
            />
          </ButtonContainer>
          <Box display="flex" justifyContent="center" marginBottom="large">
            <ResendCodeView resendSpinner={resendSpinner} onResendClicked={onResendClicked} />
          </Box>
          <Box display="flex" justifyContent="center">
            <OneTimeCodeFooterView
              onTryAnotherClicked={onTryAnotherClicked}
              hideTryAgainLink={hideTryAgainLink}
              emailOrMobileText="mobile/email"
            />
          </Box>
        </form>
      </AuthCard>
    </Page>
  );
};

const ButtonContainer = styled.div`
  margin-bottom: 24px;
  ${LG`
    margin-bottom: 32px;
  `}
`;

export { OneTimeCodeView };
